import { useTranslation } from "react-i18next"
import { useMemo } from "react"
import { FileText } from "lucide-react"
import { SearchHit } from "@/lib/searchResultParser"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import {
  searchHighlightTerms,
  searchMetadataMatch,
} from "@/lib/searchHighlight"
import SearchHighlightedText from "./SearchHighlightedText"
import CMRMetaData from "@/components/cmr/cmr-meta-data"
import {
  Message as MessageType,
  MessageDisplayType,
} from "@/reducers/messagesSlice"
import { Message } from "@/components/ui/message/message"
import { MessageBody } from "@/components/ui/message/message-body"
import useSearchResultMessage from "@/hooks/useSearchResultMessage"

export default function SearchMessageBubbleBody({
  messageHit,
}: {
  messageHit: SearchHit
}) {
  const { t } = useTranslation()
  const { document, highlight } = messageHit
  const { ref, message } = useSearchResultMessage(messageHit)
  const hasMetadata = !!(
    document.extractedMetadata || document.documentDriverContext
  )
  const metadataMessage: MessageType = message ?? {
    id: document.id,
    from: document.chat_jid,
    to: "",
    type: "groupchat",
    body: document.body || "",
    timestamp: new Date(document.timestamp / 1000).toISOString(),
    markers: [],
    reactions: [],
    displayType: MessageDisplayType.Message,
  }
  const fileUrl = message?.url || document.file?.url || document.file_url
  const content = highlight?.body?.value || document.body || ""
  const terms = useMemo(
    () => searchHighlightTerms(highlight?.body?.value || ""),
    [highlight?.body?.value],
  )
  const metadataMatch = hasMetadata
    ? searchMetadataMatch(highlight?.body?.value || "")
    : ""
  const image =
    document.file?.type === "image" ||
    /\.(png|jpe?g|webp|gif|avif|svg|bmp)(?:\?|$)/i.test(fileUrl)

  return (
    <div
      ref={ref}
      className="space-y-2"
      onClickCapture={(event) => event.preventDefault()}
    >
      {(hasMetadata || (document.hasFile && fileUrl)) && (
        <div className="flex flex-wrap items-start gap-3">
          {hasMetadata && (
            <CMRMetaData
              metadata={document.extractedMetadata}
              driverContext={document.documentDriverContext}
              message={metadataMessage}
              inSearch
              showMoreButton={false}
            />
          )}
          {fileUrl &&
            (image ? (
              <img
                src={mapFileUrlToSameOrigin(fileUrl)}
                alt={t("app.search.imageAlt")}
                loading="lazy"
                className="max-h-48 max-w-full rounded-md object-contain"
              />
            ) : (
              <span className="inline-flex max-w-full items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-xs">
                <FileText className="size-4 shrink-0" />
                <span className="truncate">
                  {fileUrl.split("/").pop()?.split("?")[0] ||
                    t("app.search.attachment")}
                </span>
              </span>
            ))}
        </div>
      )}
      {hasMetadata ? (
        metadataMatch && (
          <div className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
            <SearchHighlightedText html={metadataMatch} />
          </div>
        )
      ) : message && !message.file ? (
        <Message
          message={message}
          interactive={false}
          showAvatar={false}
          className="m-0 block"
        >
          <MessageBody highlightTerms={terms} />
        </Message>
      ) : (
        <SearchHighlightedText html={content} />
      )}
    </div>
  )
}
